import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { formatHorizonSlug, horizonDisplayLabel } from '../utils/formatHorizonSlug';
import './DataHorizonButton.css';

function horizonDisplayName(horizon, fallbackSuffix) {
  return horizonDisplayLabel(horizon, fallbackSuffix) || 'Unknown';
}

export default function DataHorizonButton({ layout = 'header' }) {
  const { user } = useAuth();
  const [horizons, setHorizons] = useState([]);
  const [currentSuffix, setCurrentSuffix] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [error, setError] = useState('');

  const loadHorizons = useCallback(async () => {
    if (!user?.id) {
      setHorizons([]);
      setCurrentSuffix('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/instances/user-suffixes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.error || data?.message || 'Failed to load data horizons');
      }
      setHorizons(Array.isArray(data.suffixes) ? data.suffixes : []);
      setCurrentSuffix(data.currentSuffix || '');
    } catch (e) {
      setError(e.message || 'Failed to load data horizons');
      setHorizons([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadHorizons();
  }, [loadHorizons]);

  useEffect(() => {
    const onHorizonChanged = (event) => {
      const suffix = event?.detail?.suffix;
      if (suffix) setCurrentSuffix(suffix);
      loadHorizons();
    };
    window.addEventListener('horizonChanged', onHorizonChanged);
    return () => window.removeEventListener('horizonChanged', onHorizonChanged);
  }, [loadHorizons]);

  const activeHorizon = useMemo(
    () => horizons.find((h) => h.suffix === currentSuffix) || horizons.find((h) => h.is_active) || null,
    [horizons, currentSuffix]
  );

  const buttonLabel = useMemo(() => {
    if (loading) return '…';
    return horizonDisplayName(activeHorizon, currentSuffix);
  }, [activeHorizon, currentSuffix, loading]);

  const handleOpen = () => {
    setModalMessage('');
    setModalOpen(true);
    if (!horizons.length && !loading) {
      setModalMessage('No data horizons are assigned to your account. Ask your superadmin if you need access.');
    } else if (horizons.length === 1) {
      setModalMessage('Ask your superadmin if you need an additional Data Horizon.');
    }
  };

  const handleClose = () => {
    if (saving) return;
    setModalOpen(false);
    setModalMessage('');
  };

  const handleSetActive = async (suffix) => {
    if (!suffix || suffix === currentSuffix) return;
    setSaving(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/instances/set-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ suffix }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to set active data horizon');
      }
      const previousSuffix = currentSuffix;
      setCurrentSuffix(suffix);
      setModalOpen(false);
      setModalMessage('');
      // Persist first, then full reload — horizon switch invalidates groups, tools, sessions, and caches.
      window.dispatchEvent(new CustomEvent('horizonChanged', {
        detail: { suffix, previousSuffix },
      }));
      window.location.reload();
      return;
    } catch (e) {
      setError(e.message || 'Failed to change data horizon');
    } finally {
      setSaving(false);
    }
  };

  if (!user?.id) return null;

  const isSidebar = layout === 'sidebar';

  const triggerButton = (
    <button
      type="button"
      className={isSidebar ? 'sidebar-horizon-btn' : 'header-horizon-btn'}
      title={`Data Horizon: ${buttonLabel}`}
      onClick={handleOpen}
      disabled={loading}
    >
      {!isSidebar && <span className="horizon-label">Data Horizon</span>}
      <span className="horizon-value">{buttonLabel}</span>
      <i className="fas fa-chevron-down" aria-hidden style={{ fontSize: 10, opacity: 0.7 }} />
    </button>
  );

  const modal = modalOpen ? (
    <div className="data-horizon-modal-backdrop" role="presentation">
      <div
        className="data-horizon-modal"
        role="dialog"
        aria-labelledby="data-horizon-modal-title"
      >
        <h3 id="data-horizon-modal-title">Data Horizon</h3>
        {error && (
          <div className="alert alert-error" style={{ marginBottom: 10 }}>
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        )}
        {modalMessage ? (
          <p className="modal-intro">{modalMessage}</p>
        ) : (
          <p className="modal-intro">Select the active data horizon for ingestion, tools, and chat.</p>
        )}
        {horizons.length > 1 && (
          <div className="horizon-options">
            {horizons.map((h) => (
              <div
                key={h.suffix}
                className={`horizon-option ${h.suffix === currentSuffix ? 'active' : ''}`}
              >
                <div>
                  <div className="horizon-option-name">{formatHorizonSlug(h.suffix)}</div>
                  {h.description && (
                    <div className="horizon-option-desc">{h.description}</div>
                  )}
                </div>
                {h.suffix === currentSuffix ? (
                  <span className="mi-hint"><i className="fas fa-check-circle" /> Active</span>
                ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary horizon-set-active-btn"
                    disabled={saving}
                    onClick={() => handleSetActive(h.suffix)}
                  >
                        {saving ? <i className="fas fa-spinner fa-spin" /> : 'Set active'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleClose} disabled={saving}>
            Close
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {isSidebar ? (
        <div className="nav-horizon-block">
          <div className="nav-horizon-label">Data Horizon</div>
          {triggerButton}
        </div>
      ) : (
        triggerButton
      )}

      {modal && (typeof document !== 'undefined'
        ? createPortal(modal, document.body)
        : modal)}
    </>
  );
}